import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';

import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  ButtonType,
  IconPosition,
  LoaderTheme,
  NotificationLayout,
  NotificationTheme,
} from '@fcom/ui-components';
import { FinnairCancelIneligibilityReason, FinnairOrder, RequestInitiator } from '@fcom/dapi/api/models';
import { ConfigService, NAME_PATTERN, PNR_PATTERN_WITH_TRAILING_WHITESPACE, RootPaths } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { isDisrupted } from '@fcom/common-booking/utils/common-booking.utils';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { DapiErrorCode, DapiHttpErrorResponse } from '@fcom/dapi';
import { RefundsService } from '@fcom/refunds-common/services/refunds.service';
import { CommonBookingFeatureState } from '@fcom/common-booking';
import { RefundCase } from '@fcom/refunds-common/interfaces/refunds.interface';
import { CommonBookingAncillaryService } from '@fcom/common-booking/modules/ancillaries/services';
import { setOrderData } from '@fcom/common-booking/store/actions/order.actions';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { getOrderByPnrAndLastName } from '@fcom/dapi/api/order-functions';
import { FinnairThemeService } from '@fcom/common/services';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

const MMB_REDIRECTION_CANCEL_INELIGIBILITY_REASONS = [
  FinnairCancelIneligibilityReason.NOT_ELIGIBLE_EMAIL_MISSING,
  FinnairCancelIneligibilityReason.ID_TICKET,
  FinnairCancelIneligibilityReason.HAS_DISRUPTION,
  FinnairCancelIneligibilityReason.HAS_SHORT_DISRUPTION,
];

@Component({
  selector: 'fin-mmb-refund-form-component',
  templateUrl: './mmb-refund-form.component.html',
  styleUrls: ['./mmb-refund-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MmbRefundFormComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private store$ = inject<Store<CommonBookingFeatureState>>(Store);
  private dapiService = inject(DapiService);
  private configService = inject(ConfigService);
  private http = inject(HttpClient);
  private refundsService = inject(RefundsService);
  private commonBookingAncillaryService = inject(CommonBookingAncillaryService);
  private themeService = inject(FinnairThemeService);

  readonly ButtonType = ButtonType;
  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconPosition = IconPosition;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly LoaderTheme = LoaderTheme;

  readonly isNewBrand = computed<boolean>(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  error$: Subject<{
    message: string;
    theme: NotificationTheme;
  }> = new Subject();
  checkRefundStatus$: Subject<void> = new Subject();

  loginForm: FormGroup<{ lastName: FormControl<string>; recLoc: FormControl<string> }>;

  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  cancelRetrieve$: Subject<boolean> = new Subject();

  private subscriptions: Subscription = new Subscription();

  ngOnInit(): void {
    this.loginForm = new FormGroup({
      lastName: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern(NAME_PATTERN),
      ]),
      recLoc: new FormControl('', [Validators.required, Validators.pattern(PNR_PATTERN_WITH_TRAILING_WHITESPACE)]),
    });

    this.subscriptions.add(
      this.checkRefundStatus$
        .pipe(
          switchMap(() => {
            return this.refundsService
              .fetchRefunds(
                this.loginForm.controls.recLoc?.value?.trim(),
                this.loginForm.controls.lastName?.value?.trim()
              )
              .pipe(
                take(1),
                catchError(() => {
                  this.error$.next({
                    message: 'mmbRefundForm.recLocAlreadyCancelledError',
                    theme: NotificationTheme.WARNING,
                  });
                  return of([]);
                })
              );
          })
        )
        .subscribe((response: RefundCase[]) => {
          if (response?.length > 0) {
            this.router.navigate([this.languageService.langValue, 'refunds']);
          } else {
            this.loading$.next(false);
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  cancelRetrieve(): void {
    this.cancelRetrieve$.next(true);
    this.loading$.next(false);
  }

  onSubmit(): void {
    if (!this.loginForm.valid) {
      this.loginForm.markAsDirty();
      this.loginForm.markAllAsTouched();
      this.loginForm.updateValueAndValidity();

      return;
    }
    this.error$.next(null);
    this.loading$.next(true);

    this.subscriptions.add(
      this.getOrder(this.loginForm.controls.recLoc?.value?.trim(), this.loginForm.controls.lastName?.value?.trim())
        .pipe(take(1), takeUntil(this.cancelRetrieve$))
        .subscribe({
          next: (order) => {
            this.store$.dispatch(setOrderData({ order }));
            this.commonBookingAncillaryService.setServiceCatalog(order, { keepQuotaLimitedProducts: true });

            if (this.isTravelAgentBooking(order)) {
              this.error$.next({
                message: 'mmbRefundForm.recLocTourOperatorError',
                theme: NotificationTheme.WARNING,
              });
            } else if (this.shouldRedirectToMmb(order)) {
              this.router.navigateByUrl(
                `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.VIEW.path}`
              );
            } else if (this.isAllowedToCancel(order)) {
              this.router.navigate([this.languageService.langValue, 'manage', 'cancel', 'review']);
            } else {
              this.error$.next({
                message: 'mmbRefundForm.tryRefundFormError',
                theme: NotificationTheme.WARNING,
              });
            }
            this.loading$.next(false);
          },
          error: (error: unknown) => {
            const errorKey = (error as DapiHttpErrorResponse)?.error?.key;
            if (errorKey === DapiErrorCode.ORDER_NOT_FOUND) {
              this.loading$.next(false);
              this.error$.next({ message: 'mmbRefundForm.recLocNotFoundError', theme: NotificationTheme.ALERT });
            } else if (errorKey === DapiErrorCode.ORDER_CANCELLED) {
              this.checkRefundStatus$.next();
            } else {
              this.error$.next({ message: 'mmbRefundForm.recLocGenericError', theme: NotificationTheme.ALERT });
              this.loading$.next(false);
            }
          },
        })
    );
  }

  private isAllowedToCancel(order: FinnairOrder): boolean {
    return order.eligibilities.voluntaryCancel.every((eligibility) => eligibility.isAllowedToUse);
  }

  private shouldRedirectToMmb(order: FinnairOrder): boolean {
    return (
      isDisrupted(order) ||
      order.eligibilities.voluntaryCancel.some((e) => MMB_REDIRECTION_CANCEL_INELIGIBILITY_REASONS.includes(e.reason))
    );
  }

  private isTravelAgentBooking(order: FinnairOrder): boolean {
    return order.eligibilities.voluntaryCancel.some(
      (eligibility) => eligibility.reason === FinnairCancelIneligibilityReason.TRAVEL_AGENT_TICKETS
    );
  }

  private getOrder(recLoc: string, lastName: string): Observable<FinnairOrder> {
    let params = new HttpParams().set('includeServiceCatalog', true);
    if (this.configService.cfg.enableAncillaryFrontendQuota) {
      params = params.set('frontEndQuota', true);
    }
    return this.dapiService
      .invokeResponse$(getOrderByPnrAndLastName, this.configService.cfg.orderUrl, {
        body: {
          id: recLoc,
          lastName: lastName,
          locale: this.languageService.localeValue,
          requestInitiator: RequestInitiator.REFUND_FORM,
        },
      })
      .pipe(
        map((response) => response.headers.get('Location')),
        switchMap((orderUrl) => this.http.get<FinnairOrder>(orderUrl, { params }))
      );
  }
}
